import React, {Component} from "react";
import {ReactSVG} from "react-svg";

export const INPUT_EXTRA_SMALL = "INPUT_EXTRA_SMALL";
export const INPUT_SMALL = "INPUT_SMALL";
export const INPUT_MEDIUM = "INPUT_MEDIUM";
export const INPUT_LARGE = "INPUT_LARGE";
export const INPUT_EXTRA_LARGE = "INPUT_EXTRA_LARGE";

export const INPUT_SIZES = [INPUT_EXTRA_SMALL, INPUT_SMALL, INPUT_MEDIUM, INPUT_LARGE, INPUT_EXTRA_LARGE];

/**
 * @class Input
 *
 * Displays a generic input
 *
 * [Confluence page]{@link https://deso.atlassian.net/wiki/spaces/Redlike/pages/144703509/Input}
 *
 * @param {string} [className] - specific classes to be applied to the input
 * @param {string} [size] - input size
 * @param {string|React.ReactElement} [labelBefore] - label to be displayed before
 * @param {string|React.ReactElement} [labelAfter] - label to be displayed after
 * @param {string} [placeholder] - placeholder in input
 * @param {svg} [svgBefore] - svg to be at the start of the input
 * @param {svg} [svgAfter] - svg to be at the end of input
 * @param {function} [svgAfterAction] - function to be called on click svgAfter element
 * @param {string} [value] - value
 * @param {string} [id] - input element id
 * @param {function} onChange - function to be called on change
 * @param {boolean} [readOnly] - is read only
 * @param {boolean} [disabled] - is disabled
 * @param {string} [type] - input type
 * @param {object} [elementProps] - other props to pass into input element
 * @param {function} [onFocus] - function to execute after focus
 * @param {function} [onBlur] - function to execute after blur
 * @param {boolean} [isValid] - turns on validation and check if the value is valid
 *
 * @example Enabled
 * <Input
 *      className="col-8 primary-input"
 *      value={this.state.phoneInputText}
 *      type="text"
 *      onChange={this.handlePhoneInputChange}
 * />
 *
 * @example Disabled
 * <Input
 *      className="col-8 primary-input"
 *      value={this.state.phoneInputText}
 *      disabled={true}
 * />
 *
 * @example ReadOnly
 * <Input
 *      className="col-8 primary-input"
 *      value={this.state.phoneInputText}
 *      readOnly={true}
 * />
 *
 * @example Slider
 * <Input
 *      className="fill-flex-space primary-input"
 *      type={"range"}
 *      min={"0"}
 *      max={"255"}
 *      value={this.state.rgb.blue}
 *      onChange={this.handleBlueChange}
 * />
 *
 *
 */
export default class Input extends Component {
    handleFocus = (event) => {
        event.target.select();
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    getValidationMark = (validationFunction) => {
        return validationFunction ? (
            <span className="padding-left-5 padding-right-5">â</span>
        ) : (
            <span className="padding-left-5 padding-right-5 color-red">â</span>
        );
    };

    getInputClass = () => {
        let variantClassName = "";

        switch (this.props.size) {
            case INPUT_EXTRA_SMALL:
                variantClassName += "xs-input ";
                break;
            case INPUT_SMALL:
                variantClassName += "s-input ";
                break;
            case INPUT_MEDIUM:
                variantClassName += "m-input ";
                break;
            case INPUT_LARGE:
                variantClassName += "l-input ";
                break;
            case INPUT_EXTRA_LARGE:
                variantClassName += "xl-input ";
                break;
            default:
                variantClassName += "m-input ";
                break;
        }

        return "general-input " + variantClassName;
    };

    render() {
        return (
            <div
                className={
                    this.getInputClass() +
                    (this.props.disabled ? " disabled-input " : "") +
                    (this.props.className ?? "")
                }
            >
                {this.props.svgBefore && (
                    <ReactSVG
                        src={this.props.svgBefore}
                        className={"input-svg" + (this.props.svgBeforeAction ? " cursor-pointer" : "")}
                        onClick={this.props.svgBeforeAction}
                    />
                )}
                {this.props.labelBefore && <div className="input-label">{this.props.labelBefore}</div>}
                <input
                    id={this.props.id}
                    placeholder={this.props.placeholder}
                    className={"input-input"}
                    checked={this.props.type === "radio" ? this.props.checked : undefined}
                    type={this.props.type ?? "text"}
                    min={this.props.min}
                    max={this.props.max}
                    value={this.props.value}
                    disabled={this.props.disabled}
                    onFocus={!this.props.disabled && !this.props.readOnly ? this.handleFocus : () => {}}
                    onBlur={!this.props.disabled && !this.props.readOnly ? this.props.onBlur : () => {}}
                    onChange={!this.props.disabled && !this.props.readOnly ? this.props.onChange : () => {}}
                    readOnly={this.props.readOnly}
                    {...this.props.elementProps}
                />
                {this.props.svgAfter && (
                    <ReactSVG
                        src={this.props.svgAfter}
                        className={"input-svg" + (this.props.svgAfterAction ? " cursor-pointer" : "")}
                        onClick={this.props.svgAfterAction}
                    />
                )}
                {this.props.labelAfter && (
                    <div className="input-label" onClick={this.props.labelAfterAction}>
                        {this.props.labelAfter}
                    </div>
                )}
                {this.props.isValid !== undefined && this.props.value && (
                    <div className="input-label">{this.getValidationMark(this.props.isValid)}</div>
                )}
            </div>
        );
    }
}
